import type { ActionItem } from "@/components/admin-dashboard/ActionItemsCard";
import type { DashboardRange } from "@/lib/dashboard-analytics";

export interface SigningSeriesPoint {
  month: string;
  mra: number;
  termsheet: number;
}

/** Monthly series for refundable security deposit (maturity-month trend). */
export interface RefundableSecuritySeriesPoint {
  month: string;
  amount: number;
  schedules: number;
}

/** Row-level detail for refundable security info sheet. */
export interface RefundableSecurityDetailRow {
  id: string;
  customerName: string;
  /** Buyback / maturity date (ISO or yyyy-MM-dd). */
  buybackDate: string;
  refundableAmount: number;
  rentalSchedule: string;
}

/** @deprecated Use RefundableSecuritySeriesPoint */
export type BuybackSeriesPoint = RefundableSecuritySeriesPoint;

export interface CashflowSeriesPoint {
  month: string;
  amount: number;
}

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const seriesRoot = (payload: unknown): Record<string, unknown> => {
  const root =
    payload && typeof payload === "object" && "data" in payload
      ? (payload as { data?: unknown }).data
      : payload;
  return root && typeof root === "object" ? (root as Record<string, unknown>) : {};
};

export function normalizeSigningTrend(
  payload: unknown
): SigningSeriesPoint[] {
  const body = seriesRoot(payload);
  const series = Array.isArray(body.series) ? body.series : [];
  return series.map((row) => {
    const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
    return {
      month: String(r.month ?? ""),
      mra: Math.round(num(r.mra)),
      termsheet: Math.round(num(r.termsheet)),
    };
  });
}

/** Normalize GET /rental-schedule/admin-dashboard-refundable-security-trend */
export function normalizeRefundableSecurityTrend(payload: unknown): {
  series: RefundableSecuritySeriesPoint[];
  details: RefundableSecurityDetailRow[];
  upcomingAmount: number;
} {
  const body = seriesRoot(payload);
  const seriesRaw = Array.isArray(body.series) ? body.series : [];
  const detailsRaw = Array.isArray(body.details) ? body.details : [];
  const series = seriesRaw.map((row) => {
    const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
    return {
      month: String(r.month ?? ""),
      amount: Math.round(
        num(
          r.amount ??
            r.refundableSecurity ??
            r.refundable_security ??
            r.refundableSecurityDeposit ??
            r.refundable_security_deposit
        )
      ),
      schedules: Math.round(num(r.schedules)),
    };
  });
  const details = detailsRaw.map((row, index) => {
    const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
    return {
      id: String(r.id ?? r.scheduleId ?? r.schedule_id ?? `rsd-${index}`),
      customerName: String(
        r.customerName ?? r.customer_name ?? r.name ?? ""
      ),
      buybackDate: String(
        r.buybackDate ??
          r.buyback_date ??
          r.maturityDate ??
          r.maturity_date ??
          r.date ??
          ""
      ),
      refundableAmount: Math.round(
        num(
          r.refundableAmount ??
            r.refundable_amount ??
            r.refundableSecurity ??
            r.refundable_security ??
            r.amount
        )
      ),
      rentalSchedule: String(r.rentalSchedule ?? r.rental_schedule ?? ""),
    };
  });
  return {
    series,
    details,
    upcomingAmount: Math.round(num(body.upcomingAmount ?? body.upcoming_amount)),
  };
}

/** @deprecated Use normalizeRefundableSecurityTrend */
export const normalizeBuybackTrend = normalizeRefundableSecurityTrend;

export function normalizeCashflowTrend(payload: unknown): CashflowSeriesPoint[] {
  const body = seriesRoot(payload);
  const series = Array.isArray(body.series) ? body.series : [];
  return series.map((row) => {
    const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
    return {
      month: String(r.month ?? ""),
      amount: Math.round(num(r.amount)),
    };
  });
}

export function normalizeActionItems(payload: unknown): ActionItem[] {
  const body = seriesRoot(payload);
  const items = Array.isArray(body.items) ? body.items : [];
  return items
    .map((row) => {
      const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
      const type = String(r.type ?? "") as ActionItem["type"];
      const priorityRaw = String(r.priority ?? "medium");
      const priority =
        priorityRaw === "high" || priorityRaw === "low" ? priorityRaw : "medium";
      return {
        id: String(r.id ?? type),
        type,
        title: String(r.title ?? ""),
        description: String(r.description ?? ""),
        priority,
        count: Math.round(num(r.count)),
        link: typeof r.link === "string" ? r.link : "/post-disbursement",
      } satisfies ActionItem;
    })
    .filter((item) => item.title && item.count > 0);
}

export type { DashboardRange };
